/*!
  Pioneer WFRIA MessageBox v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["MessageBox"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("MessageBox") < 0) {
    window.__PIONEER_GLOBAL__.components.push("MessageBox");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "className", "customStrings", "elementToFocusOnClose", "footer", "noIcon", "onClose", "open", "title", "trigger", "type"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React from "react";
import { useUniqueId } from "../common/hooks";
import { requiredIf } from "../common/proptypes";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
import { Button, IconConfirmation, IconError, IconInformationalLight, IconVerify, IconWarning, Panel } from "..";
/**
 * MessageBox is a React component with a modal overlay
 * that is used to communicate information, prompt the user for a response or both.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function MessageBox(_ref) {
  let {
    children,
    className,
    customStrings,
    elementToFocusOnClose,
    footer,
    noIcon,
    onClose,
    open,
    title,
    trigger,
    type
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    cssRoot,
    cssFirstButton,
    cssSecondButton,
    cssBodyWrapper,
    cssBodyIcon,
    cssBodyText,
    cssButtonWrapper,
    customPanelStyles
  } = extractTheme(_objectSpread({
    theme,
    noIcon
  }, otherProps));

  const componentStrings = _objectSpread(_objectSpread(_objectSpread({}, STRINGS), customStrings), {}, {
    closeButtonAriaLabel: STRINGS.closeButtonAriaLabel(title)
  });

  const messageBodyId = useUniqueId("pnr-messagebox_body-");
  /**
   * Calls `onClose` if it was supplied.
   *
   * @param {Event} e A click event.
   * @param {String} [identifier] A string to identify which footer button was clicked.
   */

  const onMessageBoxClose = (e, identifier) => {
    onClose && onClose(e, identifier);
  };
  /**
   * @param {Event} e
   */


  const onFirstButtonClose = e => {
    onMessageBoxClose(e, MessageBox.BUTTONS.FIRST);
  };
  /**
   * @param {Event} e
   */


  const onSecondButtonClose = e => {
    onMessageBoxClose(e, MessageBox.BUTTONS.SECOND);
  };
  /**
   * Returns footer buttons of kind `primary` and `standard` if not configured by user.
   * @return React.Element
   */


  const getFooterButtons = () => {
    const {
      firstButtonLabel,
      secondButtonLabel
    } = componentStrings;
    return _jsx(React.Fragment, {
      children: _jsx("div", {
        css: cssButtonWrapper,
        className: "pnr-messagebox-footer",
        children: footer ? footer : _jsxs(React.Fragment, {
          children: [_jsx(Button, {
            kind: "primary",
            label: firstButtonLabel,
            onClick: onFirstButtonClose,
            css: cssFirstButton
          }), _jsx(Button, {
            kind: "standard",
            label: secondButtonLabel,
            onClick: onSecondButtonClose,
            css: cssSecondButton
          })]
        })
      })
    });
  };

  return _jsx(Panel, _objectSpread(_objectSpread({
    "aria-describedby": messageBodyId,
    className: className,
    css: cssRoot,
    customStrings: componentStrings,
    customStyles: customPanelStyles,
    elementToFocusOnClose: elementToFocusOnClose,
    footer: getFooterButtons(),
    onClose: onClose,
    open: open,
    title: title,
    trigger: trigger
  }, otherProps), {}, {
    children: _jsxs("div", {
      id: messageBodyId,
      css: cssBodyWrapper,
      children: [type && !noIcon && _jsx("div", {
        css: cssBodyIcon,
        children: type
      }), _jsx("div", {
        css: cssBodyText,
        className: "pnr-messagebox-body-text",
        children: children
      })]
    })
  }));
}

MessageBox.BUTTONS = {
  FIRST: "first-button",
  SECOND: "second-button"
};
MessageBox.TYPES = {
  VERIFICATION: _jsx(IconVerify, {
    size: "large",
    theme: "information"
  }),
  ACTIONREQUIRED: _jsx(IconError, {
    size: "large",
    theme: "utility"
  }),
  INFORMATION: _jsx(IconInformationalLight, {
    size: "large",
    theme: "information"
  }),
  SYSTEMIMPAIRED: _jsx(IconWarning, {
    size: "large",
    theme: "impairment"
  }),
  SYSTEMOUTAGE: _jsx(IconWarning, {
    size: "large",
    theme: "impairment"
  }),
  WARNING: _jsx(IconWarning, {
    size: "large",
    theme: "impairment"
  }),
  ERROR: _jsx(IconError, {
    size: "large",
    theme: "alert"
  }),
  CONFIRMATION: _jsx(IconConfirmation, {
    size: "large",
    theme: "confirmation"
  })
};
MessageBox.propTypes = process.env.NODE_ENV !== "production" ? {
  /** This is what will be displayed as content within the MessageBox body. */
  children: PropTypes.node.isRequired,

  /** aria-label for the MessageBox - must be used when MessageBox title is not provided. */
  "aria-label": requiredIf(PropTypes.string, (props, propName) => !props.title && !props[propName], "if `title` is not configured"),

  /** @ignore The CSS class name of the root element. */
  className: PropTypes.string,

  /** Overrides for strings used within the component. */
  customStrings: PropTypes.shape({
    /**
     * Used to display custom label for first button in footer.
     * Defaults to "OK".
     * This is not applicable when `footer` prop is set.
     */
    firstButtonLabel: PropTypes.string,

    /**
     * Used to display custom label for second button in footer.
     * Defaults to `Cancel`.
     * This is not applicable when `footer` prop is set.
     */
    secondButtonLabel: PropTypes.string
  }),

  /**
   * This prop identifies the element that should receive focus after MessageBox is closed.
   * It can be a css selector or a function which returns a DOM node.
   * This prop should only be used in rare cases (when for example, the triggering element
   * that opened the MessageBox is going to be gone once the MessageBox closes.)
   * In the majority of cases, the MessageBox will automatically move the focus back to the
   * element that was used to trigger it.
   */
  elementToFocusOnClose: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * The footer of the MessageBox.
   * At least one button is required and a maximum of two buttons
   * are allowed. Button can be a primary or a standard kind.
   * If a footer is not provided, 2 buttons will be included by
   * default ("OK" and "Cancel").
   *
   */
  footer: PropTypes.node,

  /** Used to remove the icon that comes with the type. */
  noIcon: PropTypes.bool,

  /**
   * Function that will be executed after the close button
   * in the header, or the "OK" and "Cancel" buttons in the
   * footer are pressed. The first param is the event facade.
   * If the default "OK" and "Cancel" buttons are used, a second
   * param will be respectively `MessageBox.BUTTONS.FIRST` or
   * `MessageBox.BUTTONS.SECOND`.
   */
  onClose: PropTypes.func,

  /** Shows or hides the MessageBox. */
  open: PropTypes.bool,

  /** Title of the MessageBox. */
  title: PropTypes.string,

  /**
   * JSX element used to open the MessageBox.
   * It is required if `elementToFocusOnClose` is not provided.
   */
  trigger: PropTypes.node,

  /**
   * Represents the type of the message to be used within the MessageBox.
   * Defaults to a `MessageBox.TYPES.ACTIONREQUIRED` if it is not provided.
   *
   * Defined Message Types
   * ---- |
   * `MessageBox.TYPES.VERIFICATION`
   * `MessageBox.TYPES.ACTIONREQUIRED`
   * `MessageBox.TYPES.INFORMATION`
   * `MessageBox.TYPES.SYSTEMIMPAIRED`
   * `MessageBox.TYPES.SYSTEMOUTAGE`
   * `MessageBox.TYPES.WARNING`
   * `MessageBox.TYPES.ERROR`
   * `MessageBox.TYPES.CONFIRMATION`
   */
  type: PropTypes.node
} : {};
MessageBox.defaultProps = {
  "aria-label": null,
  customStrings: {},
  elementToFocusOnClose: null,
  footer: null,
  noIcon: false,
  onClose: null,
  open: false,
  title: null,
  trigger: null,
  type: MessageBox.TYPES.WARNING,
  children: null,
  className: null
};
MessageBox.displayName = "MessageBox";
export default MessageBox;